import { toItemCollections } from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type {
    ConstellationDerivation,
    ItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    deriveBaseConstellationDerivations,
    deriveBaseSpaceRenderOptions,
    deriveSyntheticItemCollectionDerivations as deriveBaseSyntheticItemCollectionDerivations,
    enrichBaseConstellationDerivations,
} from '@/workflow/_pure/derivation/baseDerivation';
import {
    buildCaseConstellationDerivations,
    buildMacroStepConnectorConstellationDerivations,
    buildWorkflowStateEntryConnectorConstellationDerivations,
} from '@/workflow/_pure/derivation/constellations';
import { deriveToolStepOuterRimDerivation } from '@/workflow/_pure/derivation/toolStepOuterRim';
import {
    requireConfig,
    resolveOperationPresentationMode,
} from '@/workflow-runner/_pure/config/config';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import { deriveActivationLinks } from '@/workflow-runner/_pure/derivation/activationLinks';
import {
    deriveCaseItemCollectionDerivations,
    deriveCaseMembership,
    deriveMacroStepConnectorItemCollectionDerivations,
    deriveResourceTypeRadialConnectorItemCollectionDerivations,
    deriveToolStepConstellationDerivation,
    deriveToolStepItemCollectionDerivation,
    deriveToolStepLayoutMetrics,
    deriveWorkflowStateEntryConnectorItemCollectionDerivations,
    deriveWorkflowStateEntryConstellationDerivations,
    deriveWorkflowStateEntryItemCollectionDerivations,
} from '@/workflow-runner/_pure/derivation/derivation';
import {
    buildToolStepItemKey,
} from '@/workflow-runner/_pure/derivation/transitionRenderState';
import type { ModeAugmentation } from '@/workflow-runner/_contract/modeAugmentation';
import type {
    PostItemTraceContext,
    PreItemTraceContext,
} from '@/workflow-runner/_contract/pipelineContext';

export function deriveSyntheticItemCollectionDerivations(
    args: Parameters<NonNullable<ModeAugmentation['deriveSyntheticItemCollectionDerivations']>>[0] & {
        preItemTraceContext: PreItemTraceContext;
    },
): ItemCollectionDerivation[] {
    const { itemCollections, input, config, preItemTraceContext } = args;
    const runnerConfig = requireConfig(config);
    const baseSyntheticItemCollectionDerivations = deriveBaseSyntheticItemCollectionDerivations({
        cosmosData: input.cosmosData,
        config,
    });

    const { traceSnapshotView } = preItemTraceContext;
    const { workflowKeyScope } = preItemTraceContext;

    const toolStepItemCollectionDerivation = preItemTraceContext.toolStepCollectionKey === undefined
        ? undefined
        : deriveToolStepItemCollectionDerivation({
            itemCollectionKey: preItemTraceContext.toolStepCollectionKey,
            cosmosData: input.cosmosData,
            config: runnerConfig,
            traceSnapshot: traceSnapshotView,
        });
    const toolStepLayoutMetrics = traceSnapshotView === undefined
        ? undefined
        : deriveToolStepLayoutMetrics({
            cosmosData: input.cosmosData,
            itemCollections,
            config: runnerConfig,
            traceSnapshot: traceSnapshotView,
        });
    const toolStepOuterRimDerivation = workflowKeyScope === undefined || toolStepLayoutMetrics === undefined
        ? undefined
        : deriveToolStepOuterRimDerivation({
            ringRadii: toolStepLayoutMetrics.ringRadii,
            ringGap: toolStepLayoutMetrics.ringGap,
            scope: workflowKeyScope,
            config,
        });

    const contextCollectionDerivations: ItemCollectionDerivation[] = [
        ...baseSyntheticItemCollectionDerivations,
        ...(toolStepItemCollectionDerivation !== undefined ? [toolStepItemCollectionDerivation] : []),
        ...(toolStepOuterRimDerivation === undefined
            ? []
            : [toolStepOuterRimDerivation.itemCollectionDerivation]),
    ];
    const contextCollections = [
        ...itemCollections,
        ...toItemCollections(contextCollectionDerivations),
    ];

    const workflowStateEntryCollectionDerivations = workflowKeyScope === undefined
        ? []
        : deriveWorkflowStateEntryItemCollectionDerivations({
            cosmosData: input.cosmosData,
            itemCollections: contextCollections,
            config,
            traceSnapshot: traceSnapshotView,
            workflowKeyScope,
        });

    const resourceTypeRadialConnectorCollectionDerivations = workflowKeyScope === undefined
        ? []
        : deriveResourceTypeRadialConnectorItemCollectionDerivations({
            cosmosData: input.cosmosData,
            itemCollections: [...contextCollections, ...toItemCollections(workflowStateEntryCollectionDerivations)],
            config: runnerConfig,
            workflowKeyScope,
            toolStepLayoutMetrics,
        });

    const caseCollectionsDerivation = workflowKeyScope === undefined
        ? { itemCollectionDerivations: [], metadataByItemKey: new Map() }
        : deriveCaseItemCollectionDerivations({
            cosmosData: input.cosmosData,
            itemCollections: contextCollections,
            config,
            traceSnapshot: traceSnapshotView,
            workflowKeyScope,
        });
    const caseCollectionDerivations = caseCollectionsDerivation.itemCollectionDerivations;

    const macroStepConnectorCollectionDerivations = workflowKeyScope === undefined
        ? []
        : deriveMacroStepConnectorItemCollectionDerivations({
            cosmosData: input.cosmosData,
            itemCollections: [
                ...contextCollections,
                ...toItemCollections([
                    ...workflowStateEntryCollectionDerivations,
                    ...caseCollectionDerivations,
                ]),
            ],
            config,
            traceSnapshot: traceSnapshotView,
            workflowKeyScope,
        });

    const workflowStateEntryConnectorCollectionDerivations = workflowKeyScope === undefined
        ? []
        : deriveWorkflowStateEntryConnectorItemCollectionDerivations({
            cosmosData: input.cosmosData,
            itemCollections: [
                ...contextCollections,
                ...toItemCollections([
                    ...workflowStateEntryCollectionDerivations,
                    ...caseCollectionDerivations,
                    ...macroStepConnectorCollectionDerivations,
                ]),
            ],
            config,
            traceSnapshot: traceSnapshotView,
            workflowKeyScope,
        });

    return [
        ...contextCollectionDerivations,
        ...resourceTypeRadialConnectorCollectionDerivations,
        ...workflowStateEntryCollectionDerivations,
        ...caseCollectionDerivations,
        ...macroStepConnectorCollectionDerivations,
        ...workflowStateEntryConnectorCollectionDerivations,
    ];
}

export function deriveConstellationDerivations(
    args: Parameters<ModeAugmentation['deriveConstellationDerivations']>[0] & {
        preItemTraceContext: PreItemTraceContext;
    },
): ConstellationDerivation[] {
    const { itemCollections, input, config, preItemTraceContext } = args;
    const runnerConfig = requireConfig(config);
    const baseConstellationDerivations = deriveBaseConstellationDerivations({ itemCollections, config });

    const { traceSnapshotView, workflowKeyScope } = preItemTraceContext;
    if (!preItemTraceContext.toolStepCollectionKey || !preItemTraceContext.toolStepConstellationKey || !workflowKeyScope) {
        return enrichBaseConstellationDerivations({
            constellationDerivations: baseConstellationDerivations,
            config,
        });
    }

    const toolStepConstellationDerivation = deriveToolStepConstellationDerivation({
        collectionKey: preItemTraceContext.toolStepCollectionKey,
        constellationKey: preItemTraceContext.toolStepConstellationKey,
        cosmosData: input.cosmosData,
        itemCollections,
        config: runnerConfig,
        traceSnapshot: traceSnapshotView,
    });
    const toolStepLayoutMetrics = traceSnapshotView === undefined
        ? undefined
        : deriveToolStepLayoutMetrics({
            cosmosData: input.cosmosData,
            itemCollections,
            config: runnerConfig,
            traceSnapshot: traceSnapshotView,
        });
    const toolStepOuterRimDerivation = toolStepLayoutMetrics === undefined
        ? undefined
        : deriveToolStepOuterRimDerivation({
            ringRadii: toolStepLayoutMetrics.ringRadii,
            ringGap: toolStepLayoutMetrics.ringGap,
            scope: workflowKeyScope,
            config,
        });

    const workflowStateEntryConstellationDerivations = deriveWorkflowStateEntryConstellationDerivations({
        cosmosData: input.cosmosData,
        itemCollections,
        config,
        traceSnapshot: traceSnapshotView,
        workflowKeyScope,
    });

    const caseCollectionsDerivation = deriveCaseItemCollectionDerivations({
        cosmosData: input.cosmosData,
        itemCollections,
        config,
        traceSnapshot: traceSnapshotView,
        workflowKeyScope,
    });

    const caseConstellationDerivations = buildCaseConstellationDerivations({
        caseItemCollectionDerivations: caseCollectionsDerivation.itemCollectionDerivations,
        caseMetadataByItemKey: caseCollectionsDerivation.metadataByItemKey,
    });

    const workflowStateEntryConnectorConstellationDerivations = buildWorkflowStateEntryConnectorConstellationDerivations({
        itemCollections,
    });

    const macroStepConnectorConstellationDerivations = buildMacroStepConnectorConstellationDerivations({
        itemCollections,
    });

    return enrichBaseConstellationDerivations({
        constellationDerivations: [
            ...baseConstellationDerivations,
            ...(toolStepConstellationDerivation === undefined ? [] : [toolStepConstellationDerivation]),
            ...(toolStepOuterRimDerivation === undefined
                ? []
                : [toolStepOuterRimDerivation.constellationDerivation]),
            ...workflowStateEntryConstellationDerivations,
            ...caseConstellationDerivations,
            ...macroStepConnectorConstellationDerivations,
            ...workflowStateEntryConnectorConstellationDerivations,
        ],
        config,
    });
}

export function deriveSpaceRenderOptions(
    args: Parameters<ModeAugmentation['deriveSpaceRenderOptions']>[0] & {
        postItemTraceContext: PostItemTraceContext;
    },
) {
    const { itemCollections, input, config, postItemTraceContext } = args;
    const baseSpaceRenderOptions = deriveBaseSpaceRenderOptions({ config });
    const runnerWorkflowConfig = requireConfig(config);
    const runnerConfig = runnerWorkflowConfig.adapterModeConfig;
    const panelOperationPresentation = resolveOperationPresentationMode(runnerWorkflowConfig) === 'panels';

    const nonActiveItemDimming = {
        enabledByConstellationKey: runnerConfig.nonActiveDimming.enabledByConstellationKey,
        opacityByConstellationKey: runnerConfig.nonActiveDimming.opacityByConstellationKey,
        enabledByLayoutKind: runnerConfig.nonActiveDimming.enabledByLayoutKind,
        opacityByLayoutKind: runnerConfig.nonActiveDimming.opacityByLayoutKind,
        defaultOpacity: runnerConfig.nonActiveDimming.defaultOpacity,
    };

    const { traceSnapshotView } = postItemTraceContext;
    const { workflowKeyScope } = postItemTraceContext;

    const activeToolStep = selectActiveToolStepFromTraceSnapshot(
        postItemTraceContext.activationTraceSnapshotView,
    );
    const activeToolStepItemKey = activeToolStep === undefined
        ? undefined
        : buildToolStepItemKey(activeToolStep.stepKey);

    const caseCollectionsDerivation = workflowKeyScope === undefined
        ? { itemCollections: [], metadataByItemKey: new Map() }
        : deriveCaseItemCollectionDerivations({
        cosmosData: input.cosmosData,
        itemCollections,
        config,
        traceSnapshot: traceSnapshotView,
        workflowKeyScope,
    });
    const activationLinks = postItemTraceContext.toolStepConstellationKey === undefined
        ? []
        : deriveActivationLinks({
            itemCollections,
            toolStepConstellationKey: postItemTraceContext.toolStepConstellationKey,
            traceSnapshot: traceSnapshotView,
            activeToolStep,
            caseMetadataByItemKey: caseCollectionsDerivation.metadataByItemKey,
            workflowKeyScope,
            caseMemberships: deriveCaseMembership({
                cosmosData: input.cosmosData,
                config: requireConfig(config),
                traceSnapshot: traceSnapshotView,
            }),
        });

    if (!activeToolStepItemKey || !postItemTraceContext.toolStepConstellationKey) {
        return {
            ...baseSpaceRenderOptions,
            activation: {
                nonActiveItemDimming,
                ...(activationLinks.length > 0 ? { activationLinks } : {}),
            },
        };
    }

    return {
        ...baseSpaceRenderOptions,
        activation: {
            nonActiveItemDimming,
            ...(panelOperationPresentation
                ? {}
                : {
                    activeItemKeyByConstellationKey: {
                        [postItemTraceContext.toolStepConstellationKey]: activeToolStepItemKey,
                    },
                }),
            ...(activationLinks.length > 0 ? { activationLinks } : {}),
        },
    };
}
